import { Check, Circle, Clock, LoaderCircle, Play, RefreshCw, Search, Square, X } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import { ActionInfo, Button, StatusMessage } from '@krp/ui-kit'

import {
  PHASE_TEXT,
  SCAN_STATE_TEXT,
  STEP_STATE_TEXT,
  STEP_TITLES,
  completedText,
  isActive,
  retryText,
  stepNumbers,
  stepShare,
} from '../model/scanText'
import { scanErrorShown } from '../model/selectors'
import {
  THRESHOLD_DEFAULT,
  THRESHOLD_MAX,
  THRESHOLD_MIN,
  type Scan,
  type ScanActions,
  type ScanStepProgress,
  type ScanVm,
  type StepState,
} from '../model/types'
import { ErrorBlock } from './ErrorBlock'
import './duplicates.css'

const STEP_ICON: Record<StepState, ReactNode> = {
  pending: <Circle size={16} aria-hidden="true" />,
  running: <LoaderCircle size={16} className="dup-steps__spin" aria-hidden="true" />,
  waiting: <Clock size={16} aria-hidden="true" />,
  done: <Check size={16} aria-hidden="true" />,
  failed: <X size={16} aria-hidden="true" />,
}

function StepLine({ step }: { step: ScanStepProgress }) {
  const share = stepShare(step)
  const retry = retryText(step)
  const started = step.state !== 'pending'
  return (
    <li data-state={step.state}>
      <span className="dup-steps__icon">{STEP_ICON[step.state]}</span>
      <div>
        <p>
          {STEP_TITLES[step.step]} <span className="dup-steps__state">— {STEP_STATE_TEXT[step.state]}</span>
        </p>
        {started && (
          <>
            <p>{stepNumbers(step)}</p>
            <progress {...(share === null ? {} : { value: share, max: 1 })} aria-label={`Ход шага «${STEP_TITLES[step.step]}»`} />
            {step.phase !== null && (
              <p className="dup-steps__line">
                {PHASE_TEXT[step.phase]}
                {retry !== null && ` · ${retry}`}
              </p>
            )}
            {step.sent !== null && <p className="dup-steps__line">Отправлено: {step.sent}</p>}
            {step.awaiting !== null && <p className="dup-steps__line">Ждём: {step.awaiting}</p>}
            {step.received !== null && <p className="dup-steps__line">Получено: {step.received}</p>}
          </>
        )}
      </div>
    </li>
  )
}

function Steps({ scan }: { scan: Scan }) {
  return (
    <ol className="dup-steps" aria-label="Шаги проверки">
      {scan.steps.map((step) => (
        <StepLine key={step.step} step={step} />
      ))}
    </ol>
  )
}

/** Порог совпадения 80–100 %: пока значение не в договоре, запуск выключен и сказано почему (И-4). */
function ThresholdStart({ scan, busy, restart, onStart }: { scan: Scan | null; busy: boolean; restart: boolean; onStart: (threshold: number) => void }) {
  const id = useId()
  const [text, setText] = useState(String(scan?.threshold ?? THRESHOLD_DEFAULT))
  const value = /^\d{1,3}$/.test(text) ? Number(text) : Number.NaN
  const valid = value >= THRESHOLD_MIN && value <= THRESHOLD_MAX
  const reason = `Порог — от ${THRESHOLD_MIN} до ${THRESHOLD_MAX} %`
  return (
    <div className="dup__threshold">
      <ActionInfo id="duplicates.source.threshold" label="Порог совпадения">
        <label htmlFor={id}>Порог совпадения, %</label>
      </ActionInfo>
      <input id={id} type="number" inputMode="numeric" min={THRESHOLD_MIN} max={THRESHOLD_MAX} step={1} value={text} onChange={(event) => setText(event.target.value)} aria-invalid={!valid} aria-describedby={`${id}-hint`} />
      <p id={`${id}-hint`} className="dup__note">
        Чем выше порог, тем меньше пар и тем они точнее. Если пар слишком много или они совсем непохожи — поднимите порог.
      </p>
      {restart ? (
        <Button variant="primary" icon={<RefreshCw size={16} aria-hidden="true" />} busy={busy} disabled={!valid} disabledReason={valid ? null : reason} onClick={() => onStart(value)} actionId="duplicates.source.scan-restart">
          Искать заново
        </Button>
      ) : (
        <Button variant="primary" icon={<Search size={16} aria-hidden="true" />} busy={busy} disabled={!valid} disabledReason={valid ? null : reason} onClick={() => onStart(value)} actionId="duplicates.source.scan-start">
          Найти дубли
        </Button>
      )}
      {!valid && <p className="dup__invalid">{reason}</p>}
    </div>
  )
}

/**
 * Панель проверки источника (И-4): нет проверки — порог и «Найти дубли»; идёт — три шага с числами и «Остановить»; готово —
 * итог и «Искать заново»; остановлена — «Продолжить»; прервалась — шаг, дословный текст, код, запрос, «Копировать», «Повторить».
 * Шаги не изображают ожидаемое сделанным: «отправлено», «ждём» и «получено» — разные слова (О-2).
 */
export function ScanPanel({ vm, actions }: { vm: ScanVm; actions: ScanActions }) {
  if (vm.loading) return <StatusMessage kind="loading" title="Узнаём, что с проверкой этого источника…" />
  if (vm.loadError !== null) {
    return (
      <ErrorBlock
        error={vm.loadError}
        action={
          <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="duplicates.source.scan-reload">
            Повторить
          </Button>
        }
      />
    )
  }
  const { scan } = vm
  return (
    <section className="dup__panel" aria-label="Проверка источника">
      {scan === null && (
        <>
          <p className="dup__panel-title">Проверка ещё не запускалась</p>
          <p className="dup__note">Что произойдёт после нажатия: 1) из источника выгрузятся наименования всех товаров, 2) в службе поиска построится карта этих наименований, 3) найдутся пары, похожие друг на друга не меньше порога. Источник при этом не меняется.</p>
          <ThresholdStart scan={null} busy={vm.busy} restart={false} onStart={actions.start} />
        </>
      )}
      {scan !== null && (
        <>
          <p className="dup__panel-title" aria-live="polite">
            {scan.state === 'completed' ? completedText(scan) : SCAN_STATE_TEXT[scan.state]}
          </p>
          {scan.state !== 'completed' && <Steps scan={scan} />}
          {scan.state === 'failed' && scan.error !== null && (
            <ErrorBlock
              error={scanErrorShown(scan.error)}
              action={
                <Button variant="primary" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.busy} onClick={scan.canResume ? actions.resume : () => actions.start(scan.threshold)} actionId="duplicates.source.scan-retry">
                  Повторить
                </Button>
              }
            />
          )}
          <div className="dup__row">
            {isActive(scan) && scan.canStop && (
              <Button variant="secondary" icon={<Square size={16} aria-hidden="true" />} busy={vm.busy} onClick={actions.stop} actionId="duplicates.source.scan-stop">
                Остановить
              </Button>
            )}
            {scan.state === 'stopped' && scan.canResume && (
              <Button variant="primary" icon={<Play size={16} aria-hidden="true" />} busy={vm.busy} onClick={actions.resume} actionId="duplicates.source.scan-resume">
                Продолжить
              </Button>
            )}
          </div>
          {scan.canRestart && <ThresholdStart scan={scan} busy={vm.busy} restart onStart={actions.start} />}
        </>
      )}
      {vm.commandError !== null && <ErrorBlock error={vm.commandError} />}
    </section>
  )
}
